import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faCheckCircle,
  faShip,
  faTruck,
  faPlane,
  faHourglass,
  faFileInvoiceDollar,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

const TrafficMiniDashboard = ({ trafficData }) => {
  // Calculate summary statistics from traffic data
  const stats = useMemo(() => {
    // Initialize counters
    const result = {
      totalTraffic: trafficData.length,
      paidCount: 0,
      openCount: 0,
      seaFreightCount: 0,
      roadFreightCount: 0,
      airFreightCount: 0,
      thisWeekCount: 0,
      thisMonthCount: 0,
      totalTaxesAmount: 0,
    };

    // Get current date info for time comparisons
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    startOfMonth.setHours(0, 0, 0, 0);

    // Process each traffic item
    trafficData.forEach((item) => {
      // Count by status
      if (item.header.detail?.traffic_data?.status === "Pagado") {
        result.paidCount++;
      } else {
        result.openCount++;
      }

      // Count by freight type
      const entryPoint = item.header.pedimento.pedimento_entry_point;
      if ([160, 510, 430].includes(Number(entryPoint))) {
        result.seaFreightCount++;
      } else if ([240].includes(Number(entryPoint))) {
        result.roadFreightCount++;
      } else if ([520, 521, 640].includes(Number(entryPoint))) {
        result.airFreightCount++;
      }

      // Count by date (this week, this month)
      const createdDate = item.header.detail?.dates_data?.date_init
        ? new Date(item.header.detail.dates_data.date_init)
        : null;

      if (createdDate) {
        if (createdDate >= startOfWeek) {
          result.thisWeekCount++;
        }
        if (createdDate >= startOfMonth) {
          result.thisMonthCount++;
        }
      }

      // Sum total taxes
      const totalTaxes = item.header.detail?.traffic_data?.total_taxes;
      if (totalTaxes && !isNaN(parseFloat(totalTaxes))) {
        result.totalTaxesAmount += parseFloat(totalTaxes);
      }
    });

    return result;
  }, [trafficData]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Traffic */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon
              icon={faBoxOpen}
              className="h-6 w-6 text-blue-600"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {stats.totalTraffic}
            </h3>
            <p className="text-sm text-gray-500">Total Traffic Items</p>
          </div>
        </div>
        <div className="mt-3 flex text-xs">
          <div className="flex items-center mr-4">
            <span className="h-2 w-2 rounded-full bg-green-400 mr-1"></span>
            <span>{stats.paidCount} Paid</span>
          </div>
          <div className="flex items-center">
            <span className="h-2 w-2 rounded-full bg-yellow-400 mr-1"></span>
            <span>{stats.openCount} Open</span>
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="h-6 w-6 text-green-600"
            />
          </div>
          <div className="ml-4">
            <div className="flex items-center">
              <div
                className="h-2.5 bg-green-500 rounded-full"
                style={{
                  width: `${
                    stats.totalTraffic > 0
                      ? (stats.paidCount / stats.totalTraffic) * 100
                      : 0
                  }%`,
                }}
              ></div>
              <div
                className="h-2.5 bg-yellow-400 rounded-full"
                style={{
                  width: `${
                    stats.totalTraffic > 0
                      ? (stats.openCount / stats.totalTraffic) * 100
                      : 0
                  }%`,
                }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-1">Status Breakdown</p>
          </div>
        </div>
        <div className="mt-3 flex justify-between text-xs">
          <div className="flex items-center">
            <span className="h-2 w-2 rounded-full bg-green-500 mr-1"></span>
            <span>
              {stats.totalTraffic > 0
                ? Math.round((stats.paidCount / stats.totalTraffic) * 100)
                : 0}
              % Paid
            </span>
          </div>
          <div className="flex items-center">
            <span className="h-2 w-2 rounded-full bg-yellow-400 mr-1"></span>
            <span>
              {stats.totalTraffic > 0
                ? Math.round((stats.openCount / stats.totalTraffic) * 100)
                : 0}
              % Open
            </span>
          </div>
        </div>
      </div>

      {/* Transport Type Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon
              icon={faTruck}
              className="h-6 w-6 text-indigo-600"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">Transport</h3>
            <p className="text-sm text-gray-500">Type Breakdown</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <div className="flex flex-col items-center">
            <FontAwesomeIcon icon={faShip} className="text-blue-600 mb-1" />
            <span className="font-semibold">{stats.seaFreightCount}</span>
            <span className="text-gray-500">Sea</span>
          </div>
          <div className="flex flex-col items-center">
            <FontAwesomeIcon icon={faTruck} className="text-green-600 mb-1" />
            <span className="font-semibold">{stats.roadFreightCount}</span>
            <span className="text-gray-500">Road</span>
          </div>
          <div className="flex flex-col items-center">
            <FontAwesomeIcon icon={faPlane} className="text-purple-600 mb-1" />
            <span className="font-semibold">{stats.airFreightCount}</span>
            <span className="text-gray-500">Air</span>
          </div>
        </div>
      </div>

      {/* Time-based Stats */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="h-6 w-6 text-orange-600"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Time Periods
            </h3>
            <p className="text-sm text-gray-500">Traffic by Date</p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="bg-gray-50 rounded p-2 flex flex-col">
            <span className="text-gray-500">This Week</span>
            <span className="font-bold text-lg">{stats.thisWeekCount}</span>
          </div>
          <div className="bg-gray-50 rounded p-2 flex flex-col">
            <span className="text-gray-500">This Month</span>
            <span className="font-bold text-lg">{stats.thisMonthCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrafficMiniDashboard;
